/**
 * Custom Variant Picker for Apple Watch Bands
 * Apple-Style Minimal UI with Fast Image Updates
 * 
 * Handles three-step guided selection:
 * 1. Gehäusegröße (Case Size)
 * 2. Adapter Farbe (Color) - filtered based on Ultra selection
 * 3. Gelenkumfang (Wrist Size)
 */

if (!customElements.get('variant-picker-custom')) {
  customElements.define(
    'variant-picker-custom',
    class VariantPickerCustom extends HTMLElement {
      constructor() {
        super();
        
        this.variantData = null;
        this.selections = {
          caseSize: null,
          color: null,
          wristSize: null
        };
        
        this.steps = {
          caseSize: null,
          color: null,
          wristSize: null
        };
        
        this.mediaGallery = null;
        this.isUpdatingImage = false;
        this.preloadedImages = new Map();
        this.popup = null;
        this.popupContent = null;
        this.sections = ['gehause', 'adapter', 'gelenkumfang'];
        
        // Cached references for performance
        this.formVariantInput = null;
        this.updateDebounceTimer = null;
        this.imageUpdateDebounceTimer = null;
      }

      connectedCallback() {
        this.initializeData();
        this.initializeSteps();
        this.initializePopup();
        this.findMediaGallery();
        this.findProductForm();
        this.bindEvents();
        this.initializeFromUrl();
        this.updateScrollIndicators();
        this.preloadVariantImages();
        this.interceptFormSubmission();
        
        // Handle browser back/forward cache (bfcache) restoration
        this.handlePageShow = this.handlePageShow.bind(this);
        window.addEventListener('pageshow', this.handlePageShow);
      }

      disconnectedCallback() {
        // Clean up event listeners
        window.removeEventListener('pageshow', this.handlePageShow);
        window.removeEventListener('resize', this.updateScrollIndicators.bind(this));
      }

      handlePageShow(event) {
        // If page is restored from bfcache, reinitialize state
        if (event.persisted) {
          // Reset cached references as they might be stale
          this.formVariantInput = null;
          this.cacheFormVariantInput();
          
          // Clear any stale visual states first
          this.resetAllOptionVisualStates();
          
          // Re-sync selections from current DOM state (radio buttons)
          this.syncSelectionsFromDOM();
          
          // Apply correct color filtering based on case size
          if (this.selections.caseSize) {
            this.filterColorOptions(this.selections.caseSize.includes('Ultra'));
          }
          
          // Update availability display
          if (this.selections.caseSize && this.selections.color) {
            this.updateWristSizeAvailability();
          }
          
          // Re-validate and update form
          if (this.selections.caseSize && this.selections.color && this.selections.wristSize) {
            const variant = this.findMatchingVariant();
            if (variant) {
              this.setVariantId(variant.id, variant);
            }
          }
        }
      }

      syncSelectionsFromDOM() {
        // Read current selections from checked radio buttons
        if (this.steps.caseSize) {
          const checkedCaseSize = this.steps.caseSize.querySelector('.variant-picker-option__input:checked');
          if (checkedCaseSize) {
            this.selections.caseSize = checkedCaseSize.value;
          }
        }
        
        if (this.steps.color) {
          const checkedColor = this.steps.color.querySelector('.variant-picker-option__input:checked');
          if (checkedColor) {
            this.selections.color = checkedColor.value;
          }
        }
        
        if (this.steps.wristSize) {
          const checkedWristSize = this.steps.wristSize.querySelector('.variant-picker-option__input:checked');
          if (checkedWristSize) {
            this.selections.wristSize = checkedWristSize.value;
          }
        }
      }

      initializeData() {
        const dataScript = this.querySelector('[data-variant-data]');
        if (dataScript) {
          try {
            this.variantData = JSON.parse(dataScript.textContent);
            
            // Cache option positions for efficient lookup
            if (this.variantData?.options) {
              this.optionPositions = {
                caseSize: this.variantData.options.caseSize?.position || 1,
                wristSize: this.variantData.options.wristSize?.position || 2,
                color: this.variantData.options.color?.position || 3
              };
            } else {
              // Fallback positions
              this.optionPositions = { caseSize: 1, wristSize: 2, color: 3 };
            }
          } catch (error) {
            console.error('Failed to parse variant data:', error);
            this.optionPositions = { caseSize: 1, wristSize: 2, color: 3 };
          }
        }
      }

      // Get variant option value by semantic name (caseSize, wristSize, color)
      getVariantOption(variant, optionType) {
        if (!variant || !this.optionPositions) return null;
        const position = this.optionPositions[optionType];
        return variant[`option${position}`];
      }

      initializeSteps() {
        this.steps.caseSize = this.querySelector('[data-step="1"]');
        this.steps.color = this.querySelector('[data-step="2"]');
        this.steps.wristSize = this.querySelector('[data-step="3"]');
      }

      initializePopup() {
        this.popup = this.querySelector('[data-guide-popup]');
        this.popupContent = this.querySelector('[data-guide-content]');
      }

      findMediaGallery() {
        const productInfo = this.closest('product-info');
        if (productInfo) {
          this.mediaGallery = productInfo.querySelector('media-gallery') || 
                             productInfo.closest('.product')?.querySelector('media-gallery');
        }
        if (!this.mediaGallery) {
          this.mediaGallery = document.querySelector('media-gallery');
        }
      }

      findProductForm() {
        // Find the product form that this variant picker is associated with
        const productInfo = this.closest('product-info');
        if (productInfo) {
          this.productFormElement = productInfo.querySelector('product-form');
          this.productForm = this.productFormElement?.querySelector('form[data-type="add-to-cart-form"]');
        }
        
        // Fallback: look for form by ID pattern
        if (!this.productForm) {
          const sectionId = this.dataset.section;
          if (sectionId) {
            this.productForm = document.querySelector(`#product-form-${sectionId}`);
            this.productFormElement = this.productForm?.closest('product-form');
          }
        }
        
        // Final fallback: find any product form on the page
        if (!this.productForm) {
          this.productFormElement = document.querySelector('product-form');
          this.productForm = this.productFormElement?.querySelector('form[data-type="add-to-cart-form"]');
        }
        
        // Cache the variant input reference for performance
        this.cacheFormVariantInput();
      }

      cacheFormVariantInput() {
        // Try to find and cache the variant input
        if (this.productForm) {
          this.formVariantInput = this.productForm.querySelector('input[name="id"].product-variant-id') ||
                                   this.productForm.querySelector('input[name="id"]');
        }
        
        if (!this.formVariantInput) {
          const sectionId = this.dataset.section;
          if (sectionId) {
            this.formVariantInput = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
          }
        }
        
        if (!this.formVariantInput) {
          this.formVariantInput = document.querySelector('product-form form[data-type="add-to-cart-form"] input[name="id"]');
        }
      }

      interceptFormSubmission() {
        if (!this.productFormElement) return;
        
        // Store reference to original submit handler
        const form = this.productForm;
        if (!form) return;

        // Add our validation before form submission
        form.addEventListener('submit', (e) => {
          if (!this.validateSelections()) {
            e.preventDefault();
            e.stopPropagation();
            this.showWarning();
            return false;
          }
          this.hideWarning();
        }, true); // Use capture phase to run before other handlers
      }

      validateSelections() {
        return this.selections.caseSize && this.selections.color && this.selections.wristSize;
      }

      showWarning() {
        // Create or show warning element
        let warning = this.querySelector('.variant-picker-warning');
        if (!warning) {
          warning = document.createElement('div');
          warning.className = 'variant-picker-warning';
          warning.innerHTML = `
            <span class="variant-picker-warning__icon">⚠</span>
            <span class="variant-picker-warning__text">Bitte wähle alle Optionen aus: Gehäusegröße, Adapter Farbe und Gelenkumfang.</span>
          `;
          // Insert before the help box or at the end of the picker
          const helpBox = this.querySelector('.variant-picker-help-box');
          if (helpBox) {
            helpBox.parentNode.insertBefore(warning, helpBox);
          } else {
            this.appendChild(warning);
          }
        }
        warning.classList.add('variant-picker-warning--visible');
        
        // Scroll warning into view
        warning.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Highlight missing steps
        this.highlightMissingSteps();
      }

      hideWarning() {
        const warning = this.querySelector('.variant-picker-warning');
        if (warning) {
          warning.classList.remove('variant-picker-warning--visible');
        }
        // Remove highlights
        this.querySelectorAll('.variant-picker-step--highlight-missing').forEach(step => {
          step.classList.remove('variant-picker-step--highlight-missing');
        });
      }

      highlightMissingSteps() {
        if (!this.selections.caseSize && this.steps.caseSize) {
          this.steps.caseSize.classList.add('variant-picker-step--highlight-missing');
        }
        if (!this.selections.color && this.steps.color) {
          this.steps.color.classList.add('variant-picker-step--highlight-missing');
        }
        if (!this.selections.wristSize && this.steps.wristSize) {
          this.steps.wristSize.classList.add('variant-picker-step--highlight-missing');
        }
      }

      // ============================================================
      // Image Preloading for Speed
      // ============================================================

      preloadVariantImages() {
        if (!this.variantData?.variants) return;

        // Strategy: Preload ONE image per color (since compositions are the same)
        // This gives us instant switching for color changes
        const colorImages = new Map(); // color -> image src
        const imagesToPreload = [];

        for (const variant of this.variantData.variants) {
          const color = this.getVariantOption(variant, 'color');
          const imageData = variant.featured_image || variant.image;
          
          // Only store first image for each color
          if (color && imageData?.src && !colorImages.has(color)) {
            colorImages.set(color, imageData.src);
            imagesToPreload.push(imageData.src);
          }
        }

        // Preload images aggressively in parallel (up to 4 at a time)
        const preloadBatch = (startIndex) => {
          const batchSize = 4;
          const endIndex = Math.min(startIndex + batchSize, imagesToPreload.length);
          
          for (let i = startIndex; i < endIndex; i++) {
            const src = imagesToPreload[i];
            const img = new Image();
            img.onload = () => {
              this.preloadedImages.set(src, true);
            };
            // Preload multiple sizes for srcset
            img.src = this.getSizedImageUrl(src, 600);
            
            // Also preload smaller size for faster initial display
            const smallImg = new Image();
            smallImg.src = this.getSizedImageUrl(src, 300);
          }
          
          // Schedule next batch
          if (endIndex < imagesToPreload.length) {
            setTimeout(() => preloadBatch(endIndex), 50);
          }
        };

        // Start preloading immediately
        preloadBatch(0);
      }

      // ============================================================
      // Event Binding
      // ============================================================

      bindEvents() {
        // Case size selection
        if (this.steps.caseSize) {
          this.steps.caseSize.querySelectorAll('.variant-picker-option__input').forEach(input => {
            input.addEventListener('change', this.handleCaseSizeChange.bind(this));
          });
        }

        // Color selection
        if (this.steps.color) {
          this.steps.color.querySelectorAll('.variant-picker-option__input').forEach(input => {
            input.addEventListener('change', this.handleColorChange.bind(this));
          });
        }

        // Wrist size selection
        if (this.steps.wristSize) {
          this.steps.wristSize.querySelectorAll('.variant-picker-option__input').forEach(input => {
            input.addEventListener('change', this.handleWristSizeChange.bind(this));
          });
          
          const scrollContainer = this.steps.wristSize.querySelector('.variant-picker-step__scroll-container');
          if (scrollContainer) {
            scrollContainer.addEventListener('scroll', this.updateScrollIndicators.bind(this));
            window.addEventListener('resize', this.updateScrollIndicators.bind(this));
          }
        }

        // Help Box link
        const helpLink = this.querySelector('[data-open-guide]');
        if (helpLink) {
          helpLink.addEventListener('click', this.openPopup.bind(this));
        }

        // Popup close button
        const closeBtn = this.querySelector('[data-guide-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', this.closePopup.bind(this));
        }

        // Close on backdrop click
        if (this.popup) {
          this.popup.addEventListener('click', (e) => {
            if (e.target === this.popup) this.closePopup();
          });
        }

        // Navigation buttons - scroll to section
        this.querySelectorAll('[data-guide-nav]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            const sectionType = e.currentTarget.dataset.guideNav;
            this.scrollToSection(sectionType);
          });
        });

        // Keyboard navigation
        document.addEventListener('keydown', this.handleKeyDown.bind(this));
      }

      // ============================================================
      // Guide Popup with Scrollable Sections
      // ============================================================

      openPopup(event) {
        event.preventDefault();
        
        if (this.popup) {
          this.popup.classList.add('variant-picker-guide-popup--open');
          document.body.style.overflow = 'hidden';
          
          // Scroll content to top
          if (this.popupContent) {
            this.popupContent.scrollTop = 0;
          }
        }
      }

      closePopup() {
        if (this.popup) {
          this.popup.classList.remove('variant-picker-guide-popup--open');
          document.body.style.overflow = '';
        }
      }

      scrollToSection(sectionType) {
        const section = this.querySelector(`[data-guide-section="${sectionType}"]`);
        const content = this.popupContent;
        
        if (section && content) {
          // Get the section's position relative to the scrollable content
          const sectionTop = section.offsetTop - content.offsetTop;
          
          // Smooth scroll to the section
          content.scrollTo({
            top: sectionTop - 10,
            behavior: 'smooth'
          });
          
          // Update active nav button
          this.querySelectorAll('[data-guide-nav]').forEach(btn => {
            btn.classList.toggle('variant-picker-guide-nav-btn--active', btn.dataset.guideNav === sectionType);
          });
        }
      }

      handleKeyDown(event) {
        if (!this.popup?.classList.contains('variant-picker-guide-popup--open')) return;
        
        if (event.key === 'Escape') {
          this.closePopup();
        }
      }

      // ============================================================
      // Selection Handlers
      // ============================================================

      initializeFromUrl() {
        // Clear any stale visual states first
        this.resetAllOptionVisualStates();
        
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        
        if (variantId && this.variantData) {
          const variant = this.variantData.variants.find(v => v.id.toString() === variantId);
          if (variant) {
            this.setSelectionsFromVariant(variant);
            return;
          }
        }
        
        // Fallback: check if DOM already has selections (from server render or bfcache)
        this.syncSelectionsFromDOM();
        if (this.selections.caseSize) {
          // DOM had selections, validate and use them
          this.enableStep(this.steps.color);
          this.filterColorOptions(this.selections.caseSize.includes('Ultra'));
          
          if (this.selections.color) {
            this.enableStep(this.steps.wristSize);
            this.updateWristSizeAvailability();
          }
          
          if (this.selections.caseSize && this.selections.color && this.selections.wristSize) {
            const variant = this.findMatchingVariant();
            if (variant) {
              this.setVariantId(variant.id, variant);
            }
          }
          return;
        }
        
        // Final fallback: use Liquid-provided selectedVariant
        if (this.variantData?.selectedVariant) {
          this.setSelectionsFromVariant(this.variantData.selectedVariant);
        }
      }

      setSelectionsFromVariant(variant) {
        if (!variant) return;
        
        // Reset all visual states first to ensure clean state
        this.resetAllOptionVisualStates();
        
        const caseSize = this.getVariantOption(variant, 'caseSize');
        const color = this.getVariantOption(variant, 'color');
        const wristSize = this.getVariantOption(variant, 'wristSize');
        
        if (caseSize) {
          this.selections.caseSize = caseSize;
          this.selectOption(this.steps.caseSize, caseSize);
          this.enableStep(this.steps.color);
          this.filterColorOptions(caseSize.includes('Ultra'));
        }
        
        if (color) {
          this.selections.color = color;
          this.selectOption(this.steps.color, color);
          this.enableStep(this.steps.wristSize);
        }
        
        if (wristSize) {
          this.selections.wristSize = wristSize;
          this.selectOption(this.steps.wristSize, wristSize);
        }
        
        this.updateSelectedLabels();
        
        // Update the variant ID in the form if all selections are made
        if (this.selections.caseSize && this.selections.color && this.selections.wristSize) {
          this.setVariantId(variant.id, variant);
        }
        
        // Update availability display for wrist size options
        this.updateWristSizeAvailability();
      }

      resetAllOptionVisualStates() {
        // Clear unavailable/nonexistent classes from all options
        this.querySelectorAll('.variant-picker-option').forEach(option => {
          option.classList.remove('variant-picker-option--unavailable');
          option.classList.remove('variant-picker-option--nonexistent');
        });
      }

      updateWristSizeAvailability() {
        // Show which wrist sizes are available for current case+color selection
        if (!this.steps.wristSize || !this.variantData || !this.selections.caseSize || !this.selections.color) return;
        
        const normalizedCaseSize = this.normalizeOptionValue(this.selections.caseSize);
        const normalizedColor = this.normalizeOptionValue(this.selections.color);
        
        this.steps.wristSize.querySelectorAll('.variant-picker-option').forEach(option => {
          const wristSize = this.normalizeOptionValue(option.dataset.optionValue);
          
          // Find if this variant exists and is available
          const variant = this.variantData.variants.find(v => 
            this.normalizeOptionValue(this.getVariantOption(v, 'caseSize')) === normalizedCaseSize && 
            this.normalizeOptionValue(this.getVariantOption(v, 'wristSize')) === wristSize && 
            this.normalizeOptionValue(this.getVariantOption(v, 'color')) === normalizedColor
          );
          
          const isAvailable = variant?.available ?? false;
          const variantExists = !!variant;
          
          // Update visual state
          option.classList.toggle('variant-picker-option--unavailable', !isAvailable && variantExists);
          option.classList.toggle('variant-picker-option--nonexistent', !variantExists);
          
          // Update the input
          const input = option.querySelector('input');
          if (input) {
            // Don't disable - let users select unavailable items to see "sold out" message
            input.dataset.available = isAvailable.toString();
          }
        });
      }

      selectOption(step, value) {
        if (!step) return;
        
        const input = step.querySelector(`input[value="${CSS.escape(value)}"]`);
        if (input) {
          input.checked = true;
          this.updateOptionStyles(step, value);
        }
      }

      updateOptionStyles(step, selectedValue) {
        step.querySelectorAll('.variant-picker-option').forEach(option => {
          const isSelected = option.dataset.optionValue === selectedValue;
          const wasSelected = option.classList.contains('variant-picker-option--selected');
          
          option.classList.toggle('variant-picker-option--selected', isSelected);
          
          // Add pulse animation on new selection
          if (isSelected && !wasSelected) {
            option.classList.add('variant-picker-option--just-selected');
            setTimeout(() => {
              option.classList.remove('variant-picker-option--just-selected');
            }, 250);
          }
        });
      }

      handleCaseSizeChange(event) {
        const value = event.target.value;
        this.selections.caseSize = value;
        
        this.selections.color = null;
        this.selections.wristSize = null;
        this.resetStep(this.steps.color);
        this.resetStep(this.steps.wristSize);
        this.disableStep(this.steps.wristSize);
        
        this.updateOptionStyles(this.steps.caseSize, value);
        
        const isUltra = value.includes('Ultra');
        this.enableStep(this.steps.color);
        this.filterColorOptions(isUltra);
        
        this.updateSelectedLabels();
        this.clearVariantId();
        this.updateVariantImage();
      }

      handleColorChange(event) {
        const value = event.target.value;
        this.selections.color = value;
        
        this.selections.wristSize = null;
        this.resetStep(this.steps.wristSize);
        
        this.updateOptionStyles(this.steps.color, value);
        this.enableStep(this.steps.wristSize);
        
        // Update wrist size availability after color selection
        this.updateWristSizeAvailability();
        
        this.updateSelectedLabels();
        this.clearVariantId();
        this.updateVariantImage();
      }

      handleWristSizeChange(event) {
        const value = event.target.value;
        this.selections.wristSize = value;
        
        this.updateOptionStyles(this.steps.wristSize, value);
        this.updateSelectedLabels();
        this.updateVariant();
      }

      filterColorOptions(isUltra) {
        if (!this.steps.color || !this.variantData) return;
        
        const colorMapping = this.variantData.colorMapping;
        const allowedColors = isUltra ? colorMapping.ultra : colorMapping.standard;
        
        this.steps.color.querySelectorAll('.variant-picker-option--color').forEach(option => {
          const colorValue = option.dataset.optionValue;
          const isAllowed = allowedColors.includes(colorValue);
          option.dataset.hidden = (!isAllowed).toString();
          option.style.display = isAllowed ? '' : 'none';
        });
      }

      enableStep(step) {
        if (!step) return;
        
        step.classList.remove('variant-picker-step--disabled');
        step.classList.add('variant-picker-step--just-enabled');
        
        setTimeout(() => {
          step.classList.remove('variant-picker-step--just-enabled');
        }, 300);
      }

      disableStep(step) {
        if (!step) return;
        step.classList.add('variant-picker-step--disabled');
      }

      resetStep(step) {
        if (!step) return;
        
        step.querySelectorAll('.variant-picker-option__input').forEach(input => {
          input.checked = false;
        });
        
        step.querySelectorAll('.variant-picker-option').forEach(option => {
          option.classList.remove('variant-picker-option--selected');
        });
        
        const selectedLabel = step.querySelector('[data-selected-value]');
        if (selectedLabel) {
          selectedLabel.textContent = '';
        }
      }

      updateSelectedLabels() {
        Object.entries(this.steps).forEach(([key, step]) => {
          if (!step) return;
          
          const label = step.querySelector('[data-selected-value]');
          if (label) {
            const value = this.selections[key];
            label.textContent = value || '';
          }
        });
      }

      // ============================================================
      // Fast Image Update Logic
      // ============================================================

      getDefaultColor() {
        if (!this.selections.caseSize || !this.variantData?.defaultColors) {
          return 'Schwarz';
        }
        
        const isUltra = this.selections.caseSize.includes('Ultra');
        return isUltra 
          ? this.variantData.defaultColors.ultra 
          : this.variantData.defaultColors.standard;
      }

      getFirstWristSize() {
        if (!this.variantData) return null;
        
        const normalizedCaseSize = this.normalizeOptionValue(this.selections.caseSize);
        const normalizedColor = this.normalizeOptionValue(this.selections.color || this.getDefaultColor());
        
        const matchingVariant = this.variantData.variants.find(v => 
          this.normalizeOptionValue(this.getVariantOption(v, 'caseSize')) === normalizedCaseSize && 
          this.normalizeOptionValue(this.getVariantOption(v, 'color')) === normalizedColor
        );
        
        return this.getVariantOption(matchingVariant, 'wristSize') || null;
      }

      findVariantForImage() {
        if (!this.variantData || !this.selections.caseSize) return null;
        
        const caseSize = this.normalizeOptionValue(this.selections.caseSize);
        const color = this.normalizeOptionValue(this.selections.color || this.getDefaultColor());
        const wristSize = this.normalizeOptionValue(this.selections.wristSize || this.getFirstWristSize());
        
        if (!wristSize) return null;
        
        return this.variantData.variants.find(v => 
          this.normalizeOptionValue(this.getVariantOption(v, 'caseSize')) === caseSize && 
          this.normalizeOptionValue(this.getVariantOption(v, 'wristSize')) === wristSize && 
          this.normalizeOptionValue(this.getVariantOption(v, 'color')) === color
        );
      }

      updateVariantImage() {
        // Minimal debounce - just prevent double-firing
        clearTimeout(this.imageUpdateDebounceTimer);
        this.imageUpdateDebounceTimer = setTimeout(() => {
          const variant = this.findVariantForImage();
          if (!variant) return;

          const imageData = variant.featured_image || variant.featured_media?.preview_image || variant.image;
          if (!imageData) return;

          this.updateProductImage(imageData);
        }, 16); // ~1 frame, just to batch rapid changes
      }

      updateProductImage(imageData) {
        if (this.isUpdatingImage) return;
        this.isUpdatingImage = true;

        // Save current scroll position to prevent page jump (especially on mobile)
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        
        // Temporarily disable smooth scrolling to prevent browser scroll behavior
        const htmlEl = document.documentElement;
        const originalScrollBehavior = htmlEl.style.scrollBehavior;
        htmlEl.style.scrollBehavior = 'auto';

        // Create a scroll lock function that we'll call multiple times
        const restoreScroll = () => {
          window.scrollTo(scrollX, scrollY);
        };

        const mainImage = this.findMainProductImage();
        
        if (mainImage && imageData) {
          // Use smaller size for faster loading (500px instead of 800px)
          const newSrc = this.getSizedImageUrl(imageData.src, 500);
          
          // Check if already preloaded
          if (this.preloadedImages.has(imageData.src)) {
            // Instant update - just swap src without any scroll effects
            mainImage.src = newSrc;
            mainImage.srcset = this.generateSrcset(imageData.src);
            if (imageData.alt) mainImage.alt = imageData.alt;
            this.isUpdatingImage = false;
            
            // Restore scroll position immediately and after next frames
            restoreScroll();
            requestAnimationFrame(() => {
              restoreScroll();
              htmlEl.style.scrollBehavior = originalScrollBehavior;
            });
          } else {
            // Quick preload then update
            this.classList.add('variant-picker-custom--updating-image');
            
            const preloadImage = new Image();
            preloadImage.onload = () => {
              mainImage.src = newSrc;
              mainImage.srcset = this.generateSrcset(imageData.src);
              if (imageData.alt) mainImage.alt = imageData.alt;
              
              this.classList.remove('variant-picker-custom--updating-image');
              this.isUpdatingImage = false;
              this.preloadedImages.set(imageData.src, true);
              
              // Restore scroll position immediately and after next frames
              restoreScroll();
              requestAnimationFrame(() => {
                restoreScroll();
                requestAnimationFrame(() => {
                  restoreScroll();
                  htmlEl.style.scrollBehavior = originalScrollBehavior;
                });
              });
            };
            
            preloadImage.onerror = () => {
              this.classList.remove('variant-picker-custom--updating-image');
              this.isUpdatingImage = false;
              htmlEl.style.scrollBehavior = originalScrollBehavior;
            };
            
            preloadImage.src = newSrc;
          }
        } else {
          this.isUpdatingImage = false;
          htmlEl.style.scrollBehavior = originalScrollBehavior;
        }

        // Update gallery without triggering scroll
        this.updateMediaGalleryNoScroll(imageData, restoreScroll);
      }

      findMainProductImage() {
        const productInfo = this.closest('product-info');
        const productContainer = productInfo?.closest('.product') || document.querySelector('.product');
        
        const selectors = [
          '.product__media-wrapper .product__media--first img',
          '.product__media-wrapper li:first-child img',
          '.product__media-wrapper img.image-magnify-lightbox',
          '.product__media-wrapper .product__media img',
          '.product__media img',
          'media-gallery .product__media img',
          'media-gallery img',
          '.product-media-container img',
          '[data-product-media-type="image"] img',
          '.product__media-list img'
        ];

        if (productContainer) {
          for (const selector of selectors) {
            const img = productContainer.querySelector(selector);
            if (img) return img;
          }
        }

        for (const selector of selectors) {
          const img = document.querySelector(selector);
          if (img) return img;
        }

        return null;
      }

      updateMediaGalleryNoScroll(imageData, restoreScrollCallback) {
        if (!this.mediaGallery || !imageData) return;

        const mediaId = imageData.id;
        if (!mediaId) return;
        
        // On mobile, we want to avoid triggering any scroll behavior from the gallery
        // The main image has already been updated, so we just need to update
        // the gallery's internal state without visual scrolling
        
        // Try to find the media item by ID
        const sectionId = this.dataset.section;
        const fullMediaId = `${sectionId}-${mediaId}`;
        
        // Check if gallery has setActiveMedia method
        if (typeof this.mediaGallery.setActiveMedia === 'function') {
          // Temporarily override scrollIntoView to prevent scrolling
          const originalScrollIntoView = Element.prototype.scrollIntoView;
          Element.prototype.scrollIntoView = function() { /* no-op */ };
          
          try {
            this.mediaGallery.setActiveMedia(fullMediaId, false); // false = don't scroll
          } catch (e) {
            // Fallback: try without section prefix
            try {
              this.mediaGallery.setActiveMedia(mediaId, false);
            } catch (e2) {
              // Ignore errors
            }
          }
          
          // Restore original scrollIntoView
          Element.prototype.scrollIntoView = originalScrollIntoView;
          
          // Ensure scroll position is maintained
          if (restoreScrollCallback) {
            restoreScrollCallback();
            requestAnimationFrame(restoreScrollCallback);
          }
        }
      }

      getSizedImageUrl(src, width) {
        if (!src) return '';
        
        // Use Shopify's image_url filter pattern for optimal CDN delivery
        // Remove any existing size parameters first
        let cleanSrc = src.replace(/_\d+x\d*(@\d+x)?/g, '');
        
        const urlParts = cleanSrc.split('?');
        const baseUrl = urlParts[0];
        const extension = baseUrl.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i);
        
        if (extension) {
          // Use exact width for CDN cache hits
          const newUrl = baseUrl.replace(extension[0], `_${width}x${extension[0]}`);
          return urlParts.length > 1 ? `${newUrl}?${urlParts[1]}` : newUrl;
        }
        
        return src;
      }

      generateSrcset(src) {
        if (!src) return '';
        
        // Fewer sizes for faster parsing
        const widths = [400, 600, 800, 1200];
        return widths
          .map(width => `${this.getSizedImageUrl(src, width)} ${width}w`)
          .join(', ');
      }

      // ============================================================
      // Variant Update
      // ============================================================

      updateVariant() {
        if (!this.selections.caseSize || !this.selections.color || !this.selections.wristSize) {
          return;
        }

        const matchingVariant = this.findMatchingVariant();
        
        if (matchingVariant) {
          // Pass variant for availability checking
          this.setVariantId(matchingVariant.id, matchingVariant);
          this.publishVariantChange(matchingVariant);
          
          const imageData = matchingVariant.featured_image || matchingVariant.image;
          if (imageData) {
            this.updateProductImage(imageData);
          }
        } else {
          this.clearVariantId();
          this.handleUnavailableVariant();
        }
      }

      findMatchingVariant() {
        if (!this.variantData) return null;
        
        // Normalize selections for comparison (handle any encoding differences)
        const normalizedCaseSize = this.normalizeOptionValue(this.selections.caseSize);
        const normalizedWristSize = this.normalizeOptionValue(this.selections.wristSize);
        const normalizedColor = this.normalizeOptionValue(this.selections.color);
        
        return this.variantData.variants.find(variant => 
          this.normalizeOptionValue(this.getVariantOption(variant, 'caseSize')) === normalizedCaseSize &&
          this.normalizeOptionValue(this.getVariantOption(variant, 'wristSize')) === normalizedWristSize &&
          this.normalizeOptionValue(this.getVariantOption(variant, 'color')) === normalizedColor
        );
      }

      normalizeOptionValue(value) {
        if (!value) return '';
        // Decode HTML entities and normalize whitespace
        const textarea = document.createElement('textarea');
        textarea.innerHTML = value;
        return textarea.value.trim();
      }

      setVariantId(variantId, variant = null) {
        // Use cached reference or find it
        if (!this.formVariantInput) {
          this.cacheFormVariantInput();
        }
        
        if (this.formVariantInput) {
          this.formVariantInput.value = variantId;
          
          // Check variant availability and update disabled state
          const isAvailable = variant ? variant.available : true;
          this.formVariantInput.disabled = !isAvailable;
          
          // Dispatch change event to notify other components
          this.formVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
          
          // Update add to cart button state
          this.updateAddToCartButton(variant);
        }
        
        // Update URL to reflect selected variant (debounced to avoid rapid history changes)
        clearTimeout(this.urlUpdateTimer);
        this.urlUpdateTimer = setTimeout(() => {
          this.updateUrl(variantId);
        }, 150);
        
        // Hide warning when valid selection is made
        this.hideWarning();
      }

      clearVariantId() {
        // Use cached reference or find it
        if (!this.formVariantInput) {
          this.cacheFormVariantInput();
        }
        
        if (this.formVariantInput) {
          this.formVariantInput.value = '';
        }
      }

      updateAddToCartButton(variant) {
        // Update the add to cart button based on variant availability
        const submitButton = this.productFormElement?.querySelector('[type="submit"]');
        const buttonText = submitButton?.querySelector('span');
        
        if (!submitButton || !buttonText) return;
        
        if (variant && !variant.available) {
          submitButton.disabled = true;
          buttonText.textContent = window.variantStrings?.soldOut || 'Ausverkauft';
        } else if (variant) {
          submitButton.disabled = false;
          buttonText.textContent = window.variantStrings?.addToCart || 'In den Warenkorb';
        }
      }

      publishVariantChange(variant) {
        // Note: We intentionally do NOT publish PUB_SUB_EVENTS.optionValueSelectionChange here.
        // The custom variant picker fully manages:
        // - Form input value (setVariantId)
        // - URL updates (updateUrl)
        // - Add-to-cart button state (updateAddToCartButton)
        // - Image updates (updateProductImage)
        // 
        // Publishing the event would cause product-info.js to make a server request
        // and override our correct state with potentially wrong data, because:
        // 1. product-info.js expects selectedOptionValues but we use a different structure
        // 2. product-info.js looks for variant-selects element which we don't have
        //
        // Instead, we publish variantChange for other components that might need it
        // (like price updates, inventory display, etc.) but with the variant already resolved.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.dataset.section,
              variant: variant,
              html: null // No HTML to parse, we handle updates directly
            }
          });
        }
      }

      updateUrl(variantId) {
        if (!variantId) return;
        
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      handleUnavailableVariant() {
        // Publish variantChange with null variant to notify other components
        // that no valid variant is selected (e.g., for price/inventory display)
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.dataset.section,
              variant: null,
              html: null
            }
          });
        }
      }

      updateScrollIndicators() {
        if (!this.steps.wristSize) return;
        
        const scrollContainer = this.steps.wristSize.querySelector('.variant-picker-step__scroll-container');
        const fadeRight = this.steps.wristSize.querySelector('.variant-picker-step__scroll-fade--right');
        
        if (!scrollContainer || !fadeRight) return;
        
        const isScrollable = scrollContainer.scrollWidth > scrollContainer.clientWidth;
        const isAtEnd = scrollContainer.scrollLeft + scrollContainer.clientWidth >= scrollContainer.scrollWidth - 5;
        
        fadeRight.classList.toggle('variant-picker-step__scroll-fade--hidden', !isScrollable || isAtEnd);
      }

      getSelections() {
        return { ...this.selections };
      }

      getSelectedVariant() {
        return this.findMatchingVariant();
      }
    }
  );
}
